import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSession } from "@/auth/SessionProvider";
import { apiGet } from "@/lib/apiClient";

type ProfileResponse = {
  profile?: {
    email?: string | null;
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
  };
  memberships?: Array<{ orgId?: string; role?: string | null }>;
};

type BillingPlanResponse = {
  planName?: string | null;
  isTrial?: boolean;
  trialDaysRemaining?: number | null;
  trialExpired?: boolean;
  seatCount?: number | null;
};

export function SettingsOverviewPage() {
  const session = useSession();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [billing, setBilling] = useState<BillingPlanResponse | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      const [profileRes, billingRes] = await Promise.all([
        apiGet<ProfileResponse>("/api/me/profile"),
        apiGet<BillingPlanResponse>("/api/billing/me/plan"),
      ]);
      if (cancelled) return;
      if (!profileRes.ok) {
        setError(profileRes.error || "Could not load profile.");
        setProfile(null);
      } else {
        setError(null);
        setProfile(profileRes.data);
      }
      if (billingRes.ok) {
        setBilling(billingRes.data ?? null);
      } else {
        setBilling(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const displayName =
    profile?.profile?.name?.trim() ||
    [profile?.profile?.firstName, profile?.profile?.lastName].filter(Boolean).join(" ") ||
    session.data?.user?.name ||
    session.data?.user?.email;

  return (
    <div className="space-y-6 p-6 sm:p-8">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Settings</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">
          Org settings hub (Phase 3). Profile loaded from <code className="text-sm">/api/me/profile</code>.
        </p>
      </div>

      {loading ? <p className="text-sm text-slate-500">Loading profile…</p> : null}
      {error ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">{error}</div>
      ) : null}

      {!loading && !error && profile ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-950/40">
            <p className="text-xs font-semibold uppercase text-slate-500">Signed in as</p>
            <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">{displayName}</p>
            <p className="text-sm text-slate-600 dark:text-slate-400">{profile.profile?.email || session.data?.user?.email}</p>
            <p className="mt-2 text-xs text-slate-500">Role: {session.data?.user?.role || "member"}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-950/40">
            <p className="text-xs font-semibold uppercase text-slate-500">Memberships</p>
            <ul className="mt-2 space-y-1 text-sm text-slate-700 dark:text-slate-300">
              {(profile.memberships || []).slice(0, 5).map((m) => (
                <li key={m.orgId}>
                  Org {m.orgId} · {m.role || "member"}
                </li>
              ))}
              {(profile.memberships || []).length === 0 ? <li>No memberships returned.</li> : null}
            </ul>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm dark:border-white/10 dark:bg-slate-950/40">
            <p className="text-xs font-semibold uppercase text-slate-500">Billing plan</p>
            {billing ? (
              <>
                <p className="mt-2 text-lg font-semibold text-slate-900 dark:text-white">
                  {billing.planName?.trim() || (billing.isTrial ? "Trial" : "Active plan")}
                </p>
                {billing.isTrial ? (
                  <p className="text-sm text-amber-700 dark:text-amber-200">
                    {billing.trialExpired
                      ? "Trial ended"
                      : typeof billing.trialDaysRemaining === "number"
                        ? `${billing.trialDaysRemaining} days left`
                        : "Trial active"}
                  </p>
                ) : null}
                {typeof billing.seatCount === "number" ? (
                  <p className="text-sm text-slate-600 dark:text-slate-400">{billing.seatCount} seats</p>
                ) : null}
              </>
            ) : (
              <p className="mt-2 text-sm text-slate-500">Plan details unavailable.</p>
            )}
          </div>
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { href: "/settings/users", label: "People", blurb: "Org user directory (read-only)" },
          { href: "/settings/groups", label: "Groups", blurb: "Profile groups list" },
          { href: "/settings/audit", label: "Audit", blurb: "Recent org audit events" },
          { href: "/settings/profiles", label: "Profiles", blurb: "Security profile templates (next)" },
        ].map((item) => (
          <Link
            key={item.href}
            to={item.href}
            className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-indigo-300 dark:border-white/10 dark:bg-slate-950/40 dark:hover:border-indigo-500/40"
          >
            <p className="font-semibold text-slate-900 dark:text-white">{item.label}</p>
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">{item.blurb}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
